<template>
    <div class="callback-container">
        <div class="registration-message">
            <p class="brand-kicker">Buddi Dashboard</p>
            <h2 v-if="success">{{ isCustomBotFlow ? 'Bot personnalisé connecté !' : 'Connexion Twitch réussie !' }}</h2>
            <h2 v-else-if="error">Erreur de connexion Twitch</h2>
            <h2 v-else>Traitement en cours...</h2>
            
            <div v-if="success && isCustomBotFlow">
                <p>Le compte bot Twitch a été lié à votre abonnement.</p>
                <p>Vous pouvez retourner sur le dashboard pour terminer la configuration.</p>
                <p>Veuillez cliquer sur le bouton ci-dessous pour fermer cette fenêtre.</p>
            </div>

            <div v-else-if="success">
                <p>Vous êtes maintenant connecté avec Twitch.</p>
                <p>Les données sont en cours de transmission à la fenêtre principale.</p>
                <p>Veuillez cliquer sur le bouton ci-dessous pour fermer cette fenêtre.</p>
            </div>
            
            <div v-else-if="error">
                <p>Une erreur s'est produite lors de la connexion avec Twitch:</p>
                <p class="error-message">{{ errorMessage }}</p>
            </div>
            
            <div v-else>
                <p>Veuillez patienter pendant que nous traitons votre authentification...</p>
                <div class="loading-spinner"></div>
            </div>
            
            <button @click="closeWindow" class="close-button" :disabled="loading">Fermer cette fenêtre</button>
        </div>
    </div>
</template>

<script>
import { subscriptionService } from '../services/subscription'

export default {
    name: 'TwitchCallback',
    data() {
        return {
            success: false,
            error: false,
            errorMessage: '',
            code: null,
            state: null,
            userData: null,
            loading: true,
            isCustomBotFlow: false
        }
    },
    methods: {
        closeWindow() {
            window.close();
        },
        // Stocker le code et l'état dans le localStorage
        storeInLocalStorage(code, state) {
            //console.log('Stockage dans localStorage:', { code, state });
            try {
                localStorage.setItem('twitch_auth_code', code);
                localStorage.setItem('twitch_auth_state_received', state);
                //console.log('Données stockées avec succès dans localStorage');
                return true;
            } catch (e) {
                console.error('Erreur lors du stockage dans localStorage:', e);
                return false;
            }
        },
        // Envoyer les données à la fenêtre principale
        sendToParentWindow(data) {
            //console.log('Envoi des données à la fenêtre principale:', data);
            try {
                if (window.opener) {
                    window.opener.postMessage(data, '*');
                    //console.log('Données envoyées avec succès à la fenêtre principale');
                    return true;
                } else {
                    console.error('Fenêtre principale non disponible');
                    return false;
                }
            } catch (e) {
                console.error('Erreur lors de l\'envoi des données à la fenêtre principale:', e);
                return false;
            }
        },
        isCustomBotState(state) {
            return typeof state === 'string' && state.startsWith('custom_bot_');
        },
        async completeCustomBotFlow() {
            try {
                const defaultRedirectUri = window.location.origin + '/twitch-callback';
                const redirectUri = process.env.VUE_APP_TWITCH_REDIRECT_URI || defaultRedirectUri;

                const response = await subscriptionService.completeCustomBotOAuth(
                    this.code,
                    this.state,
                    redirectUri
                );

                if (!response?.data?.success) {
                    throw new Error(response?.data?.error?.message || 'Erreur inconnue');
                }

                this.success = true;
                this.error = false;
            } catch (error) {
                this.error = true;
                this.success = false;
                this.errorMessage = error?.response?.data?.error?.message || error?.message || 'Échec de la connexion du bot personnalisé';
            } finally {
                this.loading = false;
            }
        }
    },
    async mounted() {
        //console.log('TwitchCallback monté, analyse des paramètres URL...');
        this.loading = true;
        
        // Récupérer le code d'autorisation et l'état de l'URL
        const urlParams = new URLSearchParams(window.location.search);
        this.code = urlParams.get('code');
        this.state = urlParams.get('state');
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');
        
        if (error) {
            // En cas d'erreur
            this.error = true;
            this.errorMessage = errorDescription || error;
            this.loading = false;
            
            // Envoyer l'erreur à la fenêtre principale
            this.sendToParentWindow({
                type: 'twitch_auth_error',
                error,
                errorDescription
            });
            
            // Stocker également l'erreur dans le localStorage comme solution de secours
            localStorage.setItem('twitch_auth_error', error);
            localStorage.setItem('twitch_auth_error_description', errorDescription || '');
        } else if (this.code && this.state) {
            if (this.isCustomBotState(this.state)) {
                this.isCustomBotFlow = true;
                await this.completeCustomBotFlow();
                return;
            }

            // En cas de succès, simplement stocker le code et l'état
            //console.log('Code Twitch reçu, transmission au composant parent');
            this.success = true;
            this.loading = false;
            
            // Double approche: localStorage et postMessage
            this.storeInLocalStorage(this.code, this.state);
            
            // Envoyer le code et l'état à la fenêtre principale
            // Le backend se chargera de l'échange du code contre un token
            this.sendToParentWindow({
                type: 'twitch_auth_success',
                code: this.code,
                state: this.state
            });
        } else {
            console.warn('Aucun code ou état trouvé dans l\'URL');
            this.loading = false;
        }
    }
}
</script>

<style scoped>
.callback-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    background:
        radial-gradient(circle at 15% 20%, rgba(237, 165, 38, 0.2), transparent 35%),
        radial-gradient(circle at 80% 90%, rgba(1, 110, 126, 0.35), transparent 40%),
        linear-gradient(135deg, var(--dark-color), var(--darker-color));
    color: white;
    padding: 24px;
}

.registration-message {
    width: min(560px, 100%);
    background: rgba(10, 18, 22, 0.86);
    border-radius: 16px;
    padding: 30px 28px;
    text-align: center;
    box-shadow: 0 20px 45px rgba(0, 0, 0, 0.45);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.12);
    animation: card-appear 0.35s ease-out;
}

.brand-kicker {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;
    padding: 6px 12px;
    border-radius: 999px;
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #f7c569;
    background: rgba(237, 165, 38, 0.12);
    border: 1px solid rgba(237, 165, 38, 0.45);
}

.registration-message h2 {
    color: white;
    margin-top: 0;
    margin-bottom: 16px;
    font-size: clamp(1.45rem, 3vw, 1.9rem);
    line-height: 1.2;
}

.registration-message p {
    margin-bottom: 12px;
    font-size: 1.02rem;
    line-height: 1.5;
    color: #d9e4e8;
}

.loading-spinner {
    display: inline-block;
    width: 42px;
    height: 42px;
    margin: 14px auto;
    border: 4px solid rgba(255, 255, 255, 0.22);
    border-radius: 50%;
    border-top-color: #eda526;
    animation: spin 1s ease-in-out infinite;
}

@keyframes card-appear {
    from {
        opacity: 0;
        transform: translateY(10px) scale(0.98);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

@media (max-width: 640px) {
    .callback-container {
        padding: 16px;
    }

.registration-message {
        padding: 24px 18px;
    }

.registration-message p {
        font-size: 0.96rem;
    }
}
</style>
